/** Mock OBS overlay for demo videos — steel theme with rider bar, attempt scores, focus card, scorecard, leaderboard. */

import { useCurrentFrame, interpolate } from "remotion";

export interface OverlayRider {
  name: string;
  nick: string;
  initials: string;
  colour: string;
  attempts: (number | null)[];
  final: number;
  currentAttempt?: number;
  maxAttempts?: number;
}

interface OverlayScreenProps {
  showFocus?: boolean;
  showScorecard?: boolean;
  showLeaderboard?: boolean;
  theme?: "steel" | "neon";
  riders?: OverlayRider[];
}

const DEFAULT_RIDERS: OverlayRider[] = [
  { name: "Harley Clifford", nick: "The King", initials: "HC", colour: "#3b82f6", attempts: [8.7, 9.2, 8.1], final: 9.2 },
  { name: "Nic Rapa", nick: "Raptor", initials: "NR", colour: "#22c55e", attempts: [7.2, 8.4, 7.8], final: 8.4 },
  { name: "Dallas Friday", nick: "D-Fri", initials: "DF", colour: "#eab308", attempts: [9.0, 8.8, 9.1], final: 9.1 },
  { name: "Amber Wing", nick: "Wings", initials: "AW", colour: "#ef4444", attempts: [6.5, 7.1, null], final: 7.1 },
  { name: "Steel Lafferty", nick: "Steele", initials: "SL", colour: "#a855f7", attempts: [8.1, 7.5, 8.3], final: 8.3 },
];

export function OverlayScreen({ showFocus = false, showScorecard = false, showLeaderboard = false, theme = "steel", riders: ridersProp }: OverlayScreenProps) {
  const frame = useCurrentFrame();

  const riders = ridersProp ?? DEFAULT_RIDERS;

  const isNeon = theme === "neon";
  const bannerBg = isNeon ? "rgba(0,0,0,0.92)" : "rgba(10,10,11,0.88)";
  const chipBg = isNeon ? "rgba(0,0,0,0.92)" : "rgba(10,10,11,0.88)";
  const chipBorder = isNeon ? "rgba(0,255,255,0.2)" : "rgba(255,255,255,0.08)";
  const accent = isNeon ? "#00ffff" : "#3b82f6";
  const liveDot = isNeon ? "#ff00ff" : "#ef4444";
  const textPrimary = "#fff";
  const textSecondary = isNeon ? "rgba(0,255,255,0.6)" : "#94a3b8";
  const textDim = isNeon ? "rgba(0,255,255,0.3)" : "#52525b";

  const barOpacity = interpolate(frame, [0, 15], [0, 1], { extrapolateRight: "clamp" });

  return (
    <div style={{ width: 1920, height: 1080, position: "relative", fontFamily: "'Inter', sans-serif" }}>
      {/* Heat name banner — top left */}
      <div style={{
        position: "absolute", top: 30, left: 40,
        display: "flex", alignItems: "center", gap: 12,
        background: bannerBg, border: `1px solid ${chipBorder}`,
        padding: "10px 24px", backdropFilter: "blur(8px)",
        opacity: barOpacity,
      }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: liveDot, boxShadow: `0 0 8px ${liveDot}` }} />
        <span style={{ fontSize: 18, fontWeight: 800, color: textPrimary, textTransform: "uppercase", letterSpacing: 1, textShadow: isNeon ? `0 0 12px ${accent}` : "none" }}>
          Finals — Men's Pro
        </span>
        <span style={{ fontSize: 12, color: textDim, fontFamily: "'JetBrains Mono', monospace" }}>
          Boardstock Invitational
        </span>
      </div>

      {/* Watermark — top right */}
      <div style={{ position: "absolute", top: 35, right: 50, fontSize: 11, color: "rgba(255,255,255,0.15)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: 1 }}>
        Scores by RALEY
      </div>

      {/* Bottom rider bar — matches real ThemedOverlay.tsx lines 103-190 */}
      <div style={{
        position: "absolute", bottom: 40, left: 40, right: 40,
        display: "flex", gap: 10, opacity: barOpacity,
      }}>
        {riders.map((r) => {
          const maxAtt = r.maxAttempts ?? r.attempts.length;
          const curAtt = r.currentAttempt ?? r.attempts.filter((a) => a !== null).length;
          // Find highest attempt score
          let highestIdx = -1;
          let highestVal = -1;
          for (let i = 0; i < maxAtt; i++) {
            const v = r.attempts[i];
            if (v != null && v > highestVal) { highestVal = v; highestIdx = i; }
          }

          return (
            <div key={r.name} style={{
              display: "flex", alignItems: "center", gap: 10,
              background: chipBg, border: `1px solid ${chipBorder}`,
              padding: "8px 14px", backdropFilter: "blur(8px)",
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: "50%",
                background: r.colour, display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, fontWeight: 700, color: "#fff", border: "2px solid rgba(255,255,255,0.2)",
              }}>
                {r.initials}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: textPrimary }}>{r.name}</div>
                <div style={{ fontSize: 10, color: textSecondary, marginTop: -1 }}>{r.nick}</div>
                {/* Attempt dots — matches ThemedOverlay lines 142-154 */}
                {maxAtt > 1 && (
                  <div style={{ display: "flex", gap: 4, marginTop: 3, alignItems: "center" }}>
                    {Array.from({ length: maxAtt }, (_, i) => {
                      const done = i + 1 <= curAtt;
                      return (
                        <div key={i} style={{
                          width: 7, height: 7, borderRadius: "50%",
                          background: done ? "#3b82f6" : "transparent",
                          border: `1.5px solid ${done ? "#3b82f6" : "rgba(255,255,255,0.25)"}`,
                          boxShadow: done ? "0 0 4px rgba(59,130,246,0.4)" : "none",
                        }} />
                      );
                    })}
                  </div>
                )}
                {/* Attempt score boxes — matches ThemedOverlay lines 156-177 */}
                {maxAtt > 1 && (
                  <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
                    {Array.from({ length: maxAtt }, (_, i) => {
                      const val = r.attempts[i];
                      const isHighest = i === highestIdx;
                      const happened = i + 1 <= curAtt;
                      return (
                        <div key={i} style={{
                          minWidth: 28, height: 20, borderRadius: 3,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                          background: isHighest ? "rgba(59,130,246,0.3)" : "rgba(255,255,255,0.05)",
                          border: `1px solid ${isHighest ? "rgba(59,130,246,0.6)" : "rgba(255,255,255,0.1)"}`,
                          color: isHighest ? "#60a5fa" : val != null ? textSecondary : "rgba(255,255,255,0.3)",
                        }}>
                          {val != null ? val.toFixed(1) : happened ? "-" : ""}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Focus card */}
      {showFocus && (
        <div style={{
          position: "absolute", left: 60, top: "50%", transform: "translateY(-50%)",
          background: isNeon ? "rgba(0,0,0,0.95)" : "rgba(10,10,11,0.92)",
          border: `1px solid ${chipBorder}`,
          padding: 32, backdropFilter: "blur(12px)", minWidth: 320,
          opacity: interpolate(frame, [0, 20], [0, 1], { extrapolateRight: "clamp" }),
          boxShadow: isNeon ? `0 0 30px rgba(0,255,255,0.1)` : "none",
        }}>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800, color: "#fff", border: `3px solid ${accent}`, marginBottom: 16 }}>HC</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: textPrimary, textShadow: isNeon ? `0 0 12px ${accent}` : "none" }}>Harley Clifford</div>
          <div style={{ fontSize: 16, color: textSecondary, marginTop: 4 }}>"The King"</div>
          <div style={{ fontSize: 14, color: textDim, fontFamily: "'JetBrains Mono', monospace", marginTop: 8 }}>#1</div>
          <div style={{ fontSize: 13, color: textDim, marginTop: 12, fontStyle: "italic", maxWidth: 260 }}>3x World Champion, known for the biggest inverts in the sport</div>
        </div>
      )}

      {/* Scorecard */}
      {showScorecard && (
        <div style={{
          position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
          background: isNeon ? "rgba(0,0,0,0.95)" : "rgba(10,10,11,0.94)",
          border: `1px solid ${chipBorder}`,
          padding: 40, backdropFilter: "blur(12px)", textAlign: "center", minWidth: 500,
          opacity: interpolate(frame, [0, 15], [0, 0.85], { extrapolateRight: "clamp" }),
        }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: textPrimary, marginBottom: 32 }}>Harley Clifford</div>
          <div style={{ display: "flex", gap: 40, justifyContent: "center", marginBottom: 32 }}>
            {[
              { label: "Judge 1", value: 9.0 },
              { label: "Judge 2", value: 9.5 },
              { label: "Judge 3", value: 9.0 },
              { label: "Judge 4", value: 9.5 },
              { label: "Judge 5", value: 9.0 },
            ].map((j) => (
              <div key={j.label} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: textDim, textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", marginBottom: 4 }}>{j.label}</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: textPrimary }}>{j.value}</div>
              </div>
            ))}
          </div>
          <div style={{ borderTop: `1px solid ${chipBorder}`, paddingTop: 20 }}>
            <div style={{ fontSize: 10, color: textDim, textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", marginBottom: 6 }}>Final (Average)</div>
            <div style={{ fontSize: 56, fontWeight: 800, color: accent, textShadow: isNeon ? `0 0 20px ${accent}` : "none" }}>9.2</div>
          </div>
        </div>
      )}

      {/* Leaderboard */}
      {showLeaderboard && (
        <div style={{
          position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
          background: isNeon ? "rgba(0,0,0,0.95)" : "rgba(10,10,11,0.94)",
          border: `1px solid ${chipBorder}`,
          padding: 40, backdropFilter: "blur(12px)", minWidth: 800,
          opacity: interpolate(frame, [0, 15], [0, 0.85], { extrapolateRight: "clamp" }),
        }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: textPrimary, textTransform: "uppercase", letterSpacing: 1, marginBottom: 24, textShadow: isNeon ? `0 0 8px ${accent}` : "none" }}>Finals — Men's Pro</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["#", "Rider", "Att 1", "Att 2", "Att 3", "Best"].map((h) => (
                  <th key={h} style={{ textAlign: h === "Rider" ? "left" : "center", padding: "8px 12px", color: textDim, fontSize: 10, textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", borderBottom: `1px solid ${chipBorder}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...riders].sort((a, b) => b.final - a.final).map((r, i) => (
                <tr key={r.name}>
                  <td style={{ padding: "10px 12px", color: textSecondary, fontFamily: "'JetBrains Mono', monospace", borderBottom: `1px solid rgba(255,255,255,0.05)`, textAlign: "center" }}>{i + 1}</td>
                  <td style={{ padding: "10px 12px", color: textPrimary, fontWeight: 600, borderBottom: `1px solid rgba(255,255,255,0.05)` }}>{r.name}</td>
                  {r.attempts.map((a, j) => (
                    <td key={j} style={{ padding: "10px 12px", textAlign: "center", color: a !== null && a === r.final ? accent : "#e4e4e7", fontWeight: a !== null && a === r.final ? 700 : 400, fontFamily: "'JetBrains Mono', monospace", borderBottom: `1px solid rgba(255,255,255,0.05)`, fontSize: 13 }}>
                      {a != null ? a.toFixed(1) : "-"}
                    </td>
                  ))}
                  <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 800, color: i === 0 ? "#eab308" : accent, fontSize: 18, fontFamily: "'JetBrains Mono', monospace", borderBottom: `1px solid rgba(255,255,255,0.05)` }}>
                    {r.final.toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
